import { ExternalLinkIcon, ShieldAlertIcon } from "lucide-react";

import type { RegulatoryUpdate } from "../../../state/policies";
import { Badge } from "../../ui/badge";
import { formatTimestamp } from "./documentTime";
import { safeExternalHref } from "../../../lib/safeExternalHref";

/**
 * What regulatory monitoring has found *for this document*.
 *
 * The list is the org's detected changes filtered to those whose analysis
 * named this policy — a change is only actionable once it is tied to the
 * document it lands on, which is the whole point of tagging jurisdictions.
 */
export function PolicyImpactCard({
  updates,
  monitoringEnabled,
}: {
  updates: ReadonlyArray<RegulatoryUpdate>;
  monitoringEnabled: boolean;
}) {
  return (
    <div className="rounded-xl border border-border/60 bg-card p-5">
      <div className="space-y-4">
        <div>
          <h3 className="text-base font-semibold tracking-tight text-foreground">
            Regulatory impact
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Monitoring findings tied to this document.
          </p>
        </div>

        {updates.length === 0 ? (
          <div className="rounded-lg border border-border/60 bg-muted/20 p-4 text-sm text-muted-foreground">
            {monitoringEnabled
              ? "No regulatory change has been matched to this document yet."
              : "Regulatory monitoring is off for this organization, so no changes are being matched to it."}
          </div>
        ) : (
          <div className="space-y-3">
            {updates.map((update) => (
              <div
                key={update.id}
                className="rounded-lg border border-border/60 bg-background/80 p-4"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 space-y-1">
                    <div className="flex items-center gap-2">
                      <ShieldAlertIcon className="size-4 shrink-0 text-muted-foreground" />
                      <span className="text-sm font-medium">{update.title}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {update.jurisdictionName}
                      <span className="mx-1.5">·</span>
                      <span title={formatTimestamp(update.detectedAt)}>
                        detected {formatTimestamp(update.detectedAt)}
                      </span>
                    </p>
                  </div>
                  {safeExternalHref(update.sourceUrl) ? (
                    <a
                      href={safeExternalHref(update.sourceUrl) ?? undefined}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex shrink-0 items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                    >
                      Source
                      <ExternalLinkIcon className="size-3" />
                    </a>
                  ) : (
                    <Badge
                      variant="outline"
                      className="rounded-full px-2 py-0 text-[10px] font-normal"
                    >
                      No source link
                    </Badge>
                  )}
                </div>

                {update.summary ? (
                  <p className="mt-3 text-sm leading-6">{update.summary}</p>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
